import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, ButtonProps, CircularProgress, Stack, Typography } from '@mui/material'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'
import type { Worker, TesseractLoggerMessage } from 'tesseract.js'
import { extractAllDigits } from '../../utils/ocr'

interface OcrScannerProps {
  onResult: (text: string) => void
  disabled?: boolean
  label?: string
  buttonProps?: Omit<ButtonProps, 'onClick' | 'disabled' | 'children'>
  onConfirming?: (isConfirming: boolean) => void
}

type RecognitionAttempt = {
  angle: number
  text: string
  digitsOnly: string
  numericCandidate: string | null
  confidence: number
  boundingArea: number
}

type BoundingCandidate = {
  digitsOnly: string
  area: number
  confidence: number
}

type TesseractBBox = {
  x0?: number
  x1?: number
  y0?: number
  y1?: number
  x?: number
  y?: number
  w?: number
  h?: number
  width?: number
  height?: number
} | null

type TesseractWord = {
  text?: string
  bbox?: TesseractBBox
  confidence?: number
} | null

type TesseractLine = {
  text?: string
  words?: TesseractWord[] | null
  bbox?: TesseractBBox
  confidence?: number
} | null

type TesseractRecognizeData = {
  text?: string
  confidence?: number
  words?: TesseractWord[] | null
  lines?: TesseractLine[] | null
} | null

type TesseractDetectData = {
  orientation?: {
    degrees?: number
  }
} | null

export default function OcrScanner({
  onResult,
  disabled,
  label,
  buttonProps,
  onConfirming
}: OcrScannerProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const workerRef = useRef<Worker | null>(null)
  const osdWorkerRef = useRef<Worker | null>(null)
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null)

  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [rawResult, setRawResult] = useState('')
  const [numericCandidate, setNumericCandidate] = useState('')
  const [cameraActive, setCameraActive] = useState(false)
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null)
  const [preferredDeviceId, setPreferredDeviceId] = useState<string | null>(null)
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
      if (osdWorkerRef.current) {
        osdWorkerRef.current.terminate()
        osdWorkerRef.current = null
      }
      stopCamera()
    }
  }, [])

  useEffect(() => {
    if (!cameraActive) {
      return
    }

    const video = cameraVideoRef.current
    const stream = activeStream
    if (!video || !stream) {
      return
    }

    if (video.srcObject !== stream) {
      video.srcObject = stream
    }

    video.play().catch(() => undefined)
  }, [cameraActive, activeStream])

  useEffect(() => {
    if (!onConfirming) {
      return
    }

    onConfirming(cameraActive || processing)
  }, [cameraActive, processing, onConfirming])

  const ensureWorker = async (): Promise<Worker> => {
    if (workerRef.current) {
      return workerRef.current
    }

    const { createWorker, PSM } = await loadTesseractModule()

    const worker = await createWorker({
      logger: (message: TesseractLoggerMessage) => {
        if (message.status === 'recognizing text' && typeof message.progress === 'number') {
          setProgress(Math.round(message.progress * 100))
        }
      }
    })

    await worker.loadLanguage('eng')
    await worker.initialize('eng')
    await worker.setParameters({
      tessedit_char_whitelist: '0123456789',
      tessedit_pageseg_mode: PSM.SPARSE_TEXT
    })

    workerRef.current = worker
    return worker
  }

  const ensureOsdWorker = async (): Promise<Worker> => {
    if (osdWorkerRef.current) {
      return osdWorkerRef.current
    }

    const { createWorker } = await loadTesseractModule()
    const worker = await createWorker()
    await worker.loadLanguage('osd')
    await worker.initialize('osd')

    osdWorkerRef.current = worker
    return worker
  }

  const requestVideoStream = async (deviceId?: string | null): Promise<MediaStream> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Media devices not supported')
    }

    const preferredConstraints: MediaTrackConstraints = {
      width: { ideal: 1920, min: 1280 },
      height: { ideal: 1080, min: 720 }
    }

    const environmentStrict: MediaStreamConstraints = {
      video: {
        ...preferredConstraints,
        facingMode: { exact: 'environment' }
      }
    }

    const environmentRelaxed: MediaStreamConstraints = {
      video: {
        ...preferredConstraints,
        facingMode: { ideal: 'environment' }
      }
    }

    if (!deviceId) {
      try {
        return await navigator.mediaDevices.getUserMedia(environmentStrict)
      } catch (error) {
        console.warn('Exact environment camera request failed, relaxing constraints', error)
        return navigator.mediaDevices.getUserMedia(environmentRelaxed)
      }
    }

    try {
      return await navigator.mediaDevices.getUserMedia({
        video: {
          ...preferredConstraints,
          deviceId: { exact: deviceId }
        }
      })
    } catch (error) {
      console.warn('Exact device request failed, falling back to environment selection', error)
      try {
        return await navigator.mediaDevices.getUserMedia(environmentStrict)
      } catch (strictError) {
        console.warn('Strict environment fallback failed, using relaxed constraints', strictError)
        return navigator.mediaDevices.getUserMedia(environmentRelaxed)
      }
    }
  }

  const attachStream = (stream: MediaStream) => {
    const track = stream.getVideoTracks()[0]
    const deviceIdFromTrack = track?.getSettings().deviceId ?? null
    setCurrentDeviceId(deviceIdFromTrack)
    if (deviceIdFromTrack) {
      setPreferredDeviceId((prev) => (prev === deviceIdFromTrack ? prev : deviceIdFromTrack))
    }

    setActiveStream((previous) => {
      if (previous && previous !== stream) {
        previous.getTracks().forEach((track) => track.stop())
      }
      return stream
    })
    setCameraActive(true)
  }

  const pickPrimaryRearCamera = async (activeId: string | null): Promise<string | null> => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return activeId
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoInputs = devices.filter(
        (device): device is MediaDeviceInfo =>
          device.kind === 'videoinput' && Boolean(device.deviceId)
      )
      if (!videoInputs.length) {
        return activeId
      }

      if (activeId && videoInputs.some((device) => device.deviceId === activeId)) {
        return activeId
      }

      const nextDeviceId = videoInputs[0]?.deviceId ?? null

      if (nextDeviceId) {
        setPreferredDeviceId((prev) => (prev === nextDeviceId ? prev : nextDeviceId))
        return nextDeviceId
      }

      return activeId
    } catch (error) {
      console.warn('Failed to enumerate devices', error)
      return activeId
    }
  }

  const switchCameraDevice = async (deviceId: string) => {
    try {
      const stream = await requestVideoStream(deviceId)
      attachStream(stream)
    } catch (error) {
      console.warn('Failed to switch camera device', error)
    }
  }

  const stopCamera = () => {
    setActiveStream((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
      return null
    })
    const video = cameraVideoRef.current
    if (video) {
      try {
        video.pause()
      } catch {
        // ignore pause failures
      }
      video.srcObject = null
    }
    setCameraActive(false)
    setCurrentDeviceId(null)
  }

  const openCamera = async () => {
    if (disabled || processing || cameraActive) {
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
      return
    }

    try {
      const initialStream = await requestVideoStream(preferredDeviceId)
      attachStream(initialStream)

      const initialId = initialStream.getVideoTracks()[0]?.getSettings().deviceId ?? null
      const bestDeviceId = await pickPrimaryRearCamera(initialId)
      if (bestDeviceId && bestDeviceId !== initialId && bestDeviceId !== currentDeviceId) {
        await switchCameraDevice(bestDeviceId)
      }
    } catch (error) {
      console.error('Failed to open inline camera', error)
      stopCamera()
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }
  }

  const clear = () => {
    stopCamera()
    setProcessing(false)
    setProgress(0)
    setRawResult('')
    setNumericCandidate('')
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = ''
      } catch {
        // ignore reset issues
      }
    }
  }

  const handleConfirm = () => {
    const value = numericCandidate || rawResult
    if (!value) {
      return
    }
    onResult(value)
    clear()
  }

  const processImageBlob = async (blob: Blob) => {
    setProcessing(true)
    setProgress(0)
    setRawResult('')
    setNumericCandidate('')

    try {
      const baseCanvas = await preprocessImageBlob(blob)

      const orientationAngle = await detectTextOrientation(baseCanvas, ensureOsdWorker)
      const anglesToTry = uniqueAngles([orientationAngle, 0, 90, 270, 180])
      const worker = await ensureWorker()

      const attempts: RecognitionAttempt[] = []
      for (const angle of anglesToTry) {
        const attempt = await recognizeCanvas(worker, baseCanvas, angle)
        attempts.push(attempt)
        if (
          attempt.numericCandidate &&
          attempt.numericCandidate.length >= 6 &&
          attempt.boundingArea > 0
        ) {
          break
        }
      }

      const bestAttempt = pickBestAttempt(attempts)
      if (bestAttempt) {
        setRawResult(bestAttempt.text)
        const candidateValue = bestAttempt.numericCandidate || bestAttempt.digitsOnly
        setNumericCandidate(candidateValue)
        setProgress(100)
      } else {
        setRawResult('')
        setNumericCandidate('')
      }
    } catch (error) {
      console.error('OCR processing failed', error)
      setRawResult('')
      setNumericCandidate('')
    } finally {
      setProcessing(false)
      if (fileInputRef.current) {
        try {
          fileInputRef.current.value = ''
        } catch {
          // ignore reset issues
        }
      }
    }
  }

  const handleCapture = async () => {
    if (processing || disabled) {
      return
    }

    const video = cameraVideoRef.current
    if (!video) {
      return
    }

    const width = video.videoWidth || video.clientWidth
    const height = video.videoHeight || video.clientHeight
    if (!width || !height) {
      console.warn('Camera not ready for capture')
      return
    }

    const captureCanvas = document.createElement('canvas')
    captureCanvas.width = width
    captureCanvas.height = height
    const ctx = captureCanvas.getContext('2d')
    if (!ctx) {
      return
    }

    ctx.drawImage(video, 0, 0, width, height)
    const blob = await canvasToBlob(captureCanvas)
    stopCamera()
    await processImageBlob(blob)
  }

  const handleCloseCamera = () => {
    stopCamera()
  }

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (processing) {
      return
    }

    await processImageBlob(file)
  }

  const progressVariant: 'determinate' | 'indeterminate' =
    progress > 0 ? 'determinate' : 'indeterminate'

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFile}
      />

      <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
        {!cameraActive && (
          <Button
            variant="contained"
            size="large"
            startIcon={<CameraAltIcon />}
            disabled={disabled || processing}
            {...buttonProps}
            onClick={openCamera}
          >
            {label ?? t('ocrScanner.scan', { defaultValue: 'Scan' })}
          </Button>
        )}

        {cameraActive && (
          <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <Box sx={{ width: '100%', maxWidth: 420 }}>
              <video
                ref={cameraVideoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', borderRadius: 8, backgroundColor: '#000' }}
              />
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                disabled={processing || disabled}
                onClick={handleCapture}
              >
                {t('ocrScanner.capture', { defaultValue: 'Capture' })}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCloseCamera}
                disabled={processing}
              >
                {t('ocrScanner.closeCamera', { defaultValue: 'Close' })}
              </Button>
            </Stack>
          </Stack>
        )}

        {processing && (
          <Stack direction="row" alignItems="center">
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                size={20}
                variant={progressVariant}
                value={progressVariant === 'determinate' ? progress : undefined}
              />
              {progressVariant === 'determinate' && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {progress}%
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {!processing && !cameraActive && (numericCandidate || rawResult) && (
          <Stack spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {t('ocrScanner.detectedLabel', { defaultValue: 'Detected' })}
            </Typography>
            <Typography variant="h5" sx={{ letterSpacing: 2 }}>
              {numericCandidate || rawResult}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckIcon />}
                onClick={handleConfirm}
              >
                {t('yes', { defaultValue: 'Yes' })}
              </Button>
              <Button variant="outlined" color="inherit" startIcon={<DeleteIcon />} onClick={clear}>
                {t('no', { defaultValue: 'No' })}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

let tesseractModulePromise: Promise<typeof import('tesseract.js')> | null = null

function loadTesseractModule() {
  if (!tesseractModulePromise) {
    tesseractModulePromise = import('tesseract.js')
  }

  return tesseractModulePromise
}

async function preprocessImageBlob(blob: Blob): Promise<HTMLCanvasElement> {
  const { image, revoke } = await loadImageFromBlob(blob)
  try {
    const maxDim = 1600
    const rawWidth = image.naturalWidth || image.width
    const rawHeight = image.naturalHeight || image.height
    const scale = Math.min(1, maxDim / Math.max(rawWidth, rawHeight))
    const width = Math.max(1, Math.round(rawWidth * scale))
    const height = Math.max(1, Math.round(rawHeight * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) {
      throw new Error('Canvas context unavailable')
    }

    ctx.drawImage(image, 0, 0, width, height)

    const imageData = ctx.getImageData(0, 0, width, height)
    const processed = applyGrayscaleAndThreshold(imageData)
    ctx.putImageData(processed, 0, 0)

    return canvas
  } finally {
    revoke()
  }
}

async function loadImageFromBlob(
  blob: Blob
): Promise<{ image: HTMLImageElement; revoke: () => void }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()

    image.onload = () => {
      resolve({
        image,
        revoke: () => {
          try {
            URL.revokeObjectURL(url)
          } catch {
            // ignore
          }
        }
      })
    }

    image.onerror = (event) => {
      try {
        URL.revokeObjectURL(url)
      } catch {
        // ignore
      }
      reject(event)
    }

    image.src = url
  })
}

async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
        return
      }
      reject(new Error('Failed to create blob from canvas'))
    }, 'image/png')
  })
}

function applyGrayscaleAndThreshold(imageData: ImageData): ImageData {
  const { data } = imageData
  const pixelCount = data.length / 4
  const grayscale = new Uint8ClampedArray(pixelCount)
  const baseHistogram = new Uint32Array(256)

  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const value = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])
    grayscale[j] = value
    baseHistogram[value] += 1
  }

  const lowerBound = intensityAtQuantile(baseHistogram, pixelCount, 0.02)
  const upperBound = intensityAtQuantile(baseHistogram, pixelCount, 0.98)
  const stretchMin = clamp(lowerBound - 5, 0, 255)
  const stretchMax = clamp(upperBound + 5, 0, 255)
  const spread = Math.max(1, stretchMax - stretchMin)
  const enhancedHistogram = new Uint32Array(256)
  const contrastFactor = 1.8

  for (let i = 0; i < grayscale.length; i++) {
    let value = grayscale[i]
    value = clamp(Math.round(((value - stretchMin) * 255) / spread), 0, 255)
    value = clamp(Math.round((value - 128) * contrastFactor + 128), 0, 255)
    grayscale[i] = value
    enhancedHistogram[value] += 1
  }

  const threshold = clamp(computeOtsuThreshold(enhancedHistogram, pixelCount), 0, 255)
  let blackPixels = applyThresholdAndCount(imageData, grayscale, threshold)
  let blackRatio = blackPixels / pixelCount

  if (blackRatio > 0.82) {
    const offset = Math.min(60, Math.round((blackRatio - 0.5) * 120))
    blackPixels = applyThresholdAndCount(imageData, grayscale, clamp(threshold + offset, 0, 255))
    blackRatio = blackPixels / pixelCount
  } else if (blackRatio < 0.18) {
    const offset = Math.min(60, Math.round((0.5 - blackRatio) * 120))
    blackPixels = applyThresholdAndCount(imageData, grayscale, clamp(threshold - offset, 0, 255))
    blackRatio = blackPixels / pixelCount
  }

  if (blackRatio > 0.9 || blackRatio < 0.1) {
    const offset = blackRatio > 0.5 ? 12 : -12
    applyThresholdAndCount(imageData, grayscale, clamp(threshold + offset, 0, 255))
  }

  return imageData
}

function applyThresholdAndCount(
  imageData: ImageData,
  grayscale: Uint8ClampedArray,
  threshold: number
): number {
  const { data } = imageData
  let blackPixels = 0

  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const value = grayscale[j] > threshold ? 255 : 0
    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
    data[i + 3] = 255
    if (value === 0) {
      blackPixels += 1
    }
  }

  return blackPixels
}

function computeOtsuThreshold(histogram: Uint32Array, totalPixels: number): number {
  let sum = 0
  for (let i = 0; i < histogram.length; i++) {
    sum += i * histogram[i]
  }

  let sumBackground = 0
  let weightBackground = 0
  let maxVariance = 0
  let threshold = 127

  for (let i = 0; i < histogram.length; i++) {
    const count = histogram[i]
    if (!count) {
      continue
    }

    weightBackground += count
    const weightForeground = totalPixels - weightBackground
    if (weightForeground <= 0) {
      break
    }

    sumBackground += i * count
    const meanBackground = sumBackground / weightBackground
    const meanForeground = (sum - sumBackground) / weightForeground
    const variance = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2

    if (variance > maxVariance) {
      maxVariance = variance
      threshold = i
    }
  }

  return threshold
}

function intensityAtQuantile(
  histogram: Uint32Array,
  totalPixels: number,
  quantile: number
): number {
  if (totalPixels <= 0) {
    return 0
  }

  const target = clamp(Math.round(totalPixels * quantile), 0, totalPixels - 1)
  let cumulative = 0

  for (let i = 0; i < histogram.length; i++) {
    cumulative += histogram[i]
    if (cumulative > target) {
      return i
    }
  }

  return histogram.length - 1
}

function clamp(value: number, minValue: number, maxValue: number): number {
  return value < minValue ? minValue : value > maxValue ? maxValue : value
}

function rotateBinaryCanvas(source: HTMLCanvasElement, angle: number): HTMLCanvasElement {
  const normalized = normalizeAngle(angle)
  if (normalized === 0) {
    return source
  }

  const radians = (normalized * Math.PI) / 180
  const swap = normalized % 180 !== 0
  const canvas = document.createElement('canvas')
  canvas.width = swap ? source.height : source.width
  canvas.height = swap ? source.width : source.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return source
  }

  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate(radians)
  ctx.drawImage(source, -source.width / 2, -source.height / 2)

  return canvas
}

function normalizeAngle(angle: number): number {
  const snapped = Math.round(angle / 90) * 90
  const wrapped = ((snapped % 360) + 360) % 360
  return wrapped
}

function uniqueAngles(angles: number[]): number[] {
  const seen = new Set<number>()
  const ordered: number[] = []

  for (const angle of angles) {
    const normalized = normalizeAngle(angle)
    if (!seen.has(normalized)) {
      seen.add(normalized)
      ordered.push(normalized)
    }
  }

  return ordered
}

async function detectTextOrientation(
  canvas: HTMLCanvasElement,
  ensureOsdWorker: () => Promise<Worker>
): Promise<number> {
  try {
    const worker = await ensureOsdWorker()
    const blob = await canvasToBlob(canvas)
    const { data } = (await worker.detect(blob)) as { data?: TesseractDetectData }
    const degrees = data?.orientation?.degrees ?? 0
    const normalized = normalizeAngle(degrees)
    if (normalized === 0) {
      return 0
    }
    return normalizeAngle(360 - normalized)
  } catch (error) {
    console.warn('Orientation detection failed, using 0 degrees', error)
    return 0
  }
}

async function recognizeCanvas(
  worker: Worker,
  baseCanvas: HTMLCanvasElement,
  angle: number
): Promise<RecognitionAttempt> {
  const normalized = normalizeAngle(angle)
  const canvas = normalized === 0 ? baseCanvas : rotateBinaryCanvas(baseCanvas, normalized)
  const blob = await canvasToBlob(canvas)
  const { data } = (await worker.recognize(blob)) as { data?: TesseractRecognizeData }
  const text = data?.text?.replace(/\s+/g, ' ').trim() ?? ''
  const sequences = text.match(/\d+/g) ?? []
  const digitsOnly = extractAllDigits(text).replace(/\s+/g, '')
  const boundingCandidate = findLargestNumericCandidate(data ?? null)
  const preferred =
    boundingCandidate?.digitsOnly ?? selectPreferredNumericSequence(sequences, digitsOnly)
  const numericCandidate = preferred ? preferred.replace(/\s+/g, '') : null

  return {
    angle: normalized,
    text,
    digitsOnly,
    numericCandidate,
    confidence: boundingCandidate?.confidence ?? data?.confidence ?? 0,
    boundingArea: boundingCandidate?.area ?? 0
  }
}

function pickBestAttempt(attempts: RecognitionAttempt[]): RecognitionAttempt | null {
  if (!attempts.length) {
    return null
  }

  return attempts.reduce<RecognitionAttempt>((best, attempt) => {
    if (attempt.boundingArea !== best.boundingArea) {
      return attempt.boundingArea > best.boundingArea ? attempt : best
    }

    const bestLength = best.numericCandidate?.length ?? 0
    const attemptLength = attempt.numericCandidate?.length ?? 0
    if (attemptLength !== bestLength) {
      return attemptLength > bestLength ? attempt : best
    }

    if (attempt.digitsOnly.length !== best.digitsOnly.length) {
      return attempt.digitsOnly.length > best.digitsOnly.length ? attempt : best
    }

    if (attempt.confidence !== best.confidence) {
      return attempt.confidence > best.confidence ? attempt : best
    }

    return best
  }, attempts[0])
}

function selectPreferredNumericSequence(sequences: string[], digitsOnly: string): string | null {
  const relevant = sequences.map((seq) => seq.replace(/\s+/g, '')).filter((seq) => seq.length >= 2)

  if (relevant.length === 0) {
    const fallback = digitsOnly.replace(/\s+/g, '')
    return fallback ? fallback : null
  }

  const bestCandidate = relevant.reduce((best, current) => {
    if (!best) {
      return current
    }

    if (current.length !== best.length) {
      return current.length > best.length ? current : best
    }

    const currentValue = Number.parseInt(current, 10)
    const bestValue = Number.parseInt(best, 10)
    return currentValue > bestValue ? current : best
  }, '')

  if (bestCandidate) {
    return bestCandidate
  }

  const fallback = digitsOnly.replace(/\s+/g, '')
  return fallback ? fallback : null
}

function findLargestNumericCandidate(result: TesseractRecognizeData): BoundingCandidate | null {
  if (!result) {
    return null
  }

  const candidates: BoundingCandidate[] = []

  const pushCandidate = (
    rawText: string | undefined,
    bbox: TesseractBBox,
    confidence: number | undefined
  ) => {
    if (!rawText || !bbox) {
      return
    }

    const digits = rawText.replace(/\D+/g, '')
    if (digits.length < 2) {
      return
    }

    const x0 = typeof bbox.x0 === 'number' ? bbox.x0 : typeof bbox.x === 'number' ? bbox.x : 0
    const widthRaw = (() => {
      if (typeof bbox.x1 === 'number') return bbox.x1 - x0
      if (typeof bbox.w === 'number') return bbox.w
      if (typeof bbox.width === 'number') return bbox.width
      return 0
    })()
    const heightRaw = (() => {
      if (typeof bbox.y1 === 'number') return bbox.y1 - (bbox.y0 ?? bbox.y ?? 0)
      if (typeof bbox.h === 'number') return bbox.h
      if (typeof bbox.height === 'number') return bbox.height
      return 0
    })()

    const width = Math.abs(widthRaw)
    const height = Math.abs(heightRaw)
    if (width <= 2 || height <= 2) {
      return
    }

    candidates.push({
      digitsOnly: digits,
      area: width * height,
      confidence: confidence ?? 0
    })
  }

  const words = Array.isArray(result?.words) ? result?.words : []
  for (const word of words) {
    if (!word) {
      continue
    }
    pushCandidate(
      typeof word.text === 'string' ? word.text : undefined,
      word.bbox ?? null,
      typeof word.confidence === 'number' ? word.confidence : undefined
    )
  }

  const lines = Array.isArray(result?.lines) ? result?.lines : []
  for (const line of lines) {
    if (!line) {
      continue
    }

    const directText = typeof line.text === 'string' ? line.text : undefined
    const wordTexts = Array.isArray(line.words)
      ? line.words.map((word) => (word && typeof word.text === 'string' ? word.text : '')).join(' ')
      : ''
    const lineText = directText ?? wordTexts

    pushCandidate(
      lineText,
      line.bbox ?? null,
      typeof line.confidence === 'number' ? line.confidence : undefined
    )
  }

  if (!candidates.length) {
    return null
  }

  return candidates.reduce<BoundingCandidate>((best, candidate) => {
    if (!best) {
      return candidate
    }

    if (candidate.area !== best.area) {
      return candidate.area > best.area ? candidate : best
    }

    if (candidate.digitsOnly.length !== best.digitsOnly.length) {
      return candidate.digitsOnly.length > best.digitsOnly.length ? candidate : best
    }

    if (candidate.confidence !== best.confidence) {
      return candidate.confidence > best.confidence ? candidate : best
    }

    return best
  }, candidates[0])
}
